import DeleteOutline from '@mui/icons-material/DeleteOutlineOutlined';
import RestartAltOutlined from '@mui/icons-material/RestartAltOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    ListSubheader,
    MenuItem,
    Button as MuiButton,
    Select,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import { useState } from 'react';
import type { DashboardWidget } from './Dashboard';
import {
    applySavedDashboardLayout,
    captureDashboardLayout,
    matchesSavedDashboardLayout,
    type SavedDashboardLayout
} from './dashboard-custom-layouts';
import {
    applyDashboardLayout,
    DASHBOARD_LAYOUT_PRESETS,
    DEFAULT_DASHBOARD_LAYOUT,
    detectDashboardLayout,
    getVisibleDashboardWidgets
} from './dashboard-layout-presets';
import './Dashboard.css';

interface DashboardSettingsProps {
    onClose: () => void;
    onSave: (widgets: DashboardWidget[], hideEmptyWidgets: boolean, customLayouts: SavedDashboardLayout[]) => void;
    widgets: DashboardWidget[];
    hideEmptyWidgets: boolean;
    counts: number[] | null;
    customLayouts: SavedDashboardLayout[];
}

export default function DashboardSettings({ onClose, onSave, widgets, hideEmptyWidgets, counts, customLayouts }: DashboardSettingsProps) {
    const [tempSettings, setTempSettings] = useState(widgets);
    const [tempHideEmptyWidgets, setTempHideEmptyWidgets] = useState(hideEmptyWidgets);
    const [savedLayouts, setSavedLayouts] = useState(customLayouts);
    const [layout, setLayout] = useState(() => {
        const saved = customLayouts.find((item) => matchesSavedDashboardLayout(widgets, hideEmptyWidgets, item));
        if (saved) return saved.id;
        return widgets.every((widget) => widget.enabled) ? detectDashboardLayout(widgets) : 'custom';
    });
    const [layoutName, setLayoutName] = useState('');
    const [nameError, setNameError] = useState('');

    const selectedNamedLayout = savedLayouts.find((saved) => saved.id === layout);
    const selectedPreset = DASHBOARD_LAYOUT_PRESETS.find((preset) => preset.id === layout);
    const previewWidgets = tempHideEmptyWidgets
        ? getVisibleDashboardWidgets(tempSettings, counts)
        : tempSettings.filter((widget) => widget.enabled);
    const previewRows = Math.max(1, ...previewWidgets.map(({ layout }) => layout.y + layout.h));

    const canSaveLayout = layout === 'custom';

    const handleSelectLayout = (id: string) => {
        const saved = savedLayouts.find((item) => item.id === id);
        if (!saved && !DASHBOARD_LAYOUT_PRESETS.some((preset) => preset.id === id)) return;

        if (saved) {
            setTempSettings((current) => applySavedDashboardLayout(current, saved));
            setTempHideEmptyWidgets(saved.hideEmptyWidgets);
        } else {
            setTempSettings((current) =>
                applyDashboardLayout(
                    current.map((widget) => ({
                        ...widget,
                        enabled: true
                    })),
                    id
                )
            );
        }

        setLayout(id);
        setLayoutName('');
        setNameError('');
    };

    const handleAddLayout = () => {
        if (!canSaveLayout) return;
        const name = layoutName.trim();
        if (!name || name.length > 60) {
            setNameError('Enter a name between 1 and 60 characters.');
            return;
        }
        if (savedLayouts.some((saved) => saved.name.toLowerCase() === name.toLowerCase())) {
            setNameError('A saved layout already uses this name.');
            return;
        }
        const saved = captureDashboardLayout(name, tempSettings, tempHideEmptyWidgets);
        setSavedLayouts((current) => [...current, saved]);
        setLayout(saved.id);
        setLayoutName('');
        setNameError('');
    };

    const handleDeleteLayout = () => {
        if (!selectedNamedLayout) return;
        setSavedLayouts((current) => current.filter((saved) => saved.id !== selectedNamedLayout.id));
        setLayout('custom');
        setLayoutName('');
        setNameError('');
    };

    const handleSave = () => {
        onSave(tempSettings, tempHideEmptyWidgets, savedLayouts);
    };

    const handleReset = () => {
        setLayout(DEFAULT_DASHBOARD_LAYOUT);
        setLayoutName('');
        setNameError('');
        setTempSettings((current) =>
            applyDashboardLayout(
                current.map((widget) => ({ ...widget, enabled: true })),
                DEFAULT_DASHBOARD_LAYOUT
            )
        );
        setTempHideEmptyWidgets(false);
    };

    const handleToggleWidget = (widgetId: string, enabled: boolean) => {
        setTempSettings((current) => current.map((widget) => (widget.id === widgetId ? { ...widget, enabled } : widget)));
        setLayout('custom');
        setNameError('');
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="dashboard-settings-title">
            <DialogTitle id="dashboard-settings-title">Dashboard Settings</DialogTitle>
            <DialogContent>
                <FormControl fullWidth margin="normal" size="small">
                    <InputLabel id="dashboard-layout-label">Layout</InputLabel>
                    <Select
                        labelId="dashboard-layout-label"
                        label="Layout"
                        value={layout}
                        inputProps={{ 'aria-describedby': 'dashboard-layout-description' }}
                        onChange={(event) => handleSelectLayout(event.target.value)}>
                        <MenuItem value="custom" disabled>
                            Custom
                        </MenuItem>
                        <ListSubheader>Default layouts</ListSubheader>
                        {DASHBOARD_LAYOUT_PRESETS.map((preset) => (
                            <MenuItem key={preset.id} value={preset.id}>
                                {preset.label}
                            </MenuItem>
                        ))}
                        {savedLayouts.length > 0 && <ListSubheader>My layouts</ListSubheader>}
                        {savedLayouts.map((saved) => (
                            <MenuItem key={saved.id} value={saved.id}>
                                {saved.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography id="dashboard-layout-description" variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                    {selectedNamedLayout
                        ? 'Your saved arrangement, widget visibility, and hide-empty preference.'
                        : (selectedPreset?.description ?? 'Your custom arrangement. Save it with a name to reuse it later.')}
                </Typography>

                {canSaveLayout && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                        <TextField
                            label="New layout name"
                            size="small"
                            fullWidth
                            value={layoutName}
                            error={Boolean(nameError)}
                            helperText={nameError || 'Name your current arrangement.'}
                            onChange={(event) => {
                                setLayoutName(event.target.value);
                                setNameError('');
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleAddLayout();
                                }
                            }}
                        />
                        <MuiButton
                            variant="outlined"
                            startIcon={<SaveOutlined />}
                            disabled={!layoutName.trim()}
                            onClick={handleAddLayout}
                            sx={{ whiteSpace: 'nowrap', minHeight: 40 }}>
                            Add
                        </MuiButton>
                    </Box>
                )}
                {selectedNamedLayout && (
                    <MuiButton color="error" size="small" startIcon={<DeleteOutline />} onClick={handleDeleteLayout} sx={{ mb: 1 }}>
                        Delete saved layout
                    </MuiButton>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Save applies all changes, including added or deleted layouts. Cancel discards them.
                </Typography>

                <Typography
                    id="dashboard-layout-preview-title"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75 }}>
                    Layout preview
                </Typography>
                <Box
                    role="group"
                    aria-labelledby="dashboard-layout-preview-title"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                        gridTemplateRows: `repeat(${previewRows}, 10px)`,
                        gap: '3px',
                        p: 1,
                        mb: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: 'background.default'
                    }}>
                    {previewWidgets.map((widget) => (
                        <Box
                            key={widget.id}
                            sx={{
                                gridColumn: `${widget.layout.x + 1} / span ${widget.layout.w}`,
                                gridRow: `${widget.layout.y + 1} / span ${widget.layout.h}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 0,
                                p: 0.5,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                            }}>
                            <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.3, textAlign: 'center', overflowWrap: 'anywhere' }}>
                                {widget.label}
                            </Typography>
                        </Box>
                    ))}
                    {previewWidgets.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1', py: 1, textAlign: 'center' }}>
                            {tempSettings.some((widget) => widget.enabled) ? 'No matching records' : 'No widgets enabled'}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch checked={tempHideEmptyWidgets} onChange={(event) => setTempHideEmptyWidgets(event.target.checked)} />
                        }
                        label="Hide empty widgets"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Empty widgets return automatically when matching data arrives.
                    </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Visible widgets
                </Typography>
                <div className="dashboard-settings">
                    {tempSettings.map((widget) => (
                        <div key={widget.id} className="dashboard-setting">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={widget.enabled}
                                        onChange={(event) => handleToggleWidget(widget.id, event.target.checked)}
                                    />
                                }
                                label={widget.label}
                            />
                        </div>
                    ))}
                </div>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 0.5, flexWrap: 'wrap' }}>
                <MuiButton startIcon={<RestartAltOutlined />} onClick={handleReset} sx={{ mr: 'auto' }}>
                    Reset layout
                </MuiButton>
                <MuiButton variant="text" onClick={onClose}>
                    Cancel
                </MuiButton>
                <MuiButton variant="contained" onClick={handleSave}>
                    Save
                </MuiButton>
            </DialogActions>
        </Dialog>
    );
}
